import { observer } from 'mobx-react-lite';
import { TradePlanData } from '../../../../../../../../classes/tradePlan/tradePlanData';
import { useTradePlanFormStore } from '../../../../../../../../classes/tradePlan/tradePlanProvider';
import { colors } from '../../../../../../../../colorVars';
import { NumericalInput } from '../../../../../../../atomic/atomicComponentLibrary/numericalInput/NumericalInput';
import { UnitSelector, ValueUnit } from '../../../../../../../atomic/atomicComponentLibrary/unitSelector/UnitSelector';
import { Body3, Body4, fontSizes } from '../../../../../../../atomic/text/Text';
import style from '../SimpleTacticStyle.module.css';

const isFuturesSymbol = (symbol: string) => symbol?.startsWith('/') ?? false;

export const SimpleRatchetBracket = observer(() => {
  const tradePlanForm: TradePlanData = useTradePlanFormStore();
  const defaultFn = () => {};
  const tactic = tradePlanForm.exitTactics.RATCHET_BRACKET;
  const isEnabled = tactic ?? false;
  const isFutures = isFuturesSymbol(tradePlanForm.symbol ?? '');
  const isOcoMode = tactic?.mode === 'OCO_ANCHORED';

  // EQUAL_INTERVAL fields
  const targetPerBracket = tactic?.targetPerBracket ?? 1;
  const setTargetPerBracket = tactic?.setTargetPerBracket ?? defaultFn;
  const stopPerBracket = tactic?.stopPerBracket ?? 1;
  const setStopPerBracket = tactic?.setStopPerBracket ?? defaultFn;

  // OCO_ANCHORED fields
  const ocoStopPrice = tactic?.ocoStopPrice ?? 0;
  const setOcoStopPrice = tactic?.setOcoStopPrice ?? defaultFn;
  const ocoTargetPrice = tactic?.ocoTargetPrice ?? 0;
  const setOcoTargetPrice = tactic?.setOcoTargetPrice ?? defaultFn;
  const ocoNumberOfTargets = tactic?.ocoNumberOfTargets ?? 1;
  const setOcoNumberOfTargets = tactic?.setOcoNumberOfTargets ?? defaultFn;

  // Shared fields
  const qtyBasisValuePerBracket = tactic?.qtyBasisValuePerBracket ?? 25;
  const setQtyBasisValuePerBracket = tactic?.setQtyBasisValuePerBracket ?? defaultFn;
  const valueUnit: ValueUnit = (tactic?.valueUnit as ValueUnit) ?? 'DOLLARS';
  const setValueUnit = tactic?.setValueUnit ?? defaultFn;
  const mode = tactic?.mode ?? 'EQUAL_INTERVAL';
  const setMode = tactic?.setMode ?? defaultFn;

  const dollarIcon = isFutures ? undefined : '$';

  const handleModeChange = (newMode: string) => {
    setMode(newMode);
    if (newMode === 'OCO_ANCHORED') {
      tactic?.setOcoStopPrice(0);
      tactic?.setOcoTargetPrice(0);
      tactic?.setOcoNumberOfTargets(1);
    } else {
      tactic?.setOcoStopPrice(null);
      tactic?.setOcoTargetPrice(null);
      tactic?.setOcoNumberOfTargets(null);
    }
  };

  return (
    <div className={style.container}>
      <div className={style.labelAndCheckbox}>
        <input
          style={{ margin: '0 5px 0 0' }}
          type="checkbox"
          checked={isEnabled}
          onChange={() => {
            if (isEnabled) {
              tradePlanForm.removeExitTactic('RATCHET_BRACKET');
            } else {
              tradePlanForm.addExitTactic('RATCHET_BRACKET');
            }
          }}
        />
        <Body3 fontWeight={700} color={colors.lightGrey}>
          Ratchet Bracket
        </Body3>
      </div>

      {isEnabled && (
        <div className={style.controlsThree}>
          <div className={style.labelAndControl}>
            <Body4 color={colors.lightGrey}>Mode</Body4>
            <select
              value={mode}
              onChange={(e) => handleModeChange(e.target.value)}
              disabled={!isEnabled}
              style={{ fontSize: fontSizes.body4, backgroundColor: 'grey', color: 'white' }}
            >
              <option value="EQUAL_INTERVAL">Equal Interval</option>
              <option value="OCO_ANCHORED">OCO Anchored</option>
            </select>
          </div>

          {isOcoMode ? (
            <>
              <div className={style.labelAndControl}>
                <Body4 color={colors.lightGrey}>Stop Price</Body4>
                <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                  {isFutures && (
                    <UnitSelector
                      value={valueUnit}
                      onChange={setValueUnit}
                      disabled={!isEnabled}
                    />
                  )}
                  <NumericalInput
                    disabled={!isEnabled}
                    icon={dollarIcon}
                    width="50px"
                    maxHeight="23px"
                    fontSize={fontSizes.body4}
                    fontColor="white"
                    backgroundColor="grey"
                    controlledValue={ocoStopPrice}
                    onChange={setOcoStopPrice}
                  />
                </div>
              </div>

              <div className={style.labelAndControl}>
                <Body4 color={colors.lightGrey}>Target Price</Body4>
                <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                  {isFutures && (
                    <UnitSelector
                      value={valueUnit}
                      onChange={setValueUnit}
                      disabled={!isEnabled}
                    />
                  )}
                  <NumericalInput
                    disabled={!isEnabled}
                    icon={dollarIcon}
                    width="50px"
                    maxHeight="23px"
                    fontSize={fontSizes.body4}
                    fontColor="white"
                    backgroundColor="grey"
                    controlledValue={ocoTargetPrice}
                    onChange={setOcoTargetPrice}
                  />
                </div>
              </div>

              <div className={style.labelAndControl}>
                <Body4 color={colors.lightGrey}>Num. Targets</Body4>
                <NumericalInput
                  disabled={!isEnabled}
                  icon="#"
                  width="30px"
                  maxHeight="23px"
                  fontSize={fontSizes.body4}
                  fontColor="white"
                  backgroundColor="grey"
                  controlledValue={ocoNumberOfTargets}
                  onChange={(val: number) =>
                    setOcoNumberOfTargets(Math.max(1, Math.round(Number(val))))
                  }
                />
              </div>
            </>
          ) : (
            <>
              <div className={style.labelAndControl}>
                <Body4 color={colors.lightGrey}>Target</Body4>
                <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                  {isFutures && (
                    <UnitSelector
                      value={valueUnit}
                      onChange={setValueUnit}
                      disabled={!isEnabled}
                    />
                  )}
                  <NumericalInput
                    disabled={!isEnabled}
                    icon={dollarIcon}
                    width="30px"
                    maxHeight="23px"
                    fontSize={fontSizes.body4}
                    fontColor="white"
                    backgroundColor="grey"
                    controlledValue={targetPerBracket}
                    onChange={setTargetPerBracket}
                  />
                </div>
              </div>

              <div className={style.labelAndControl}>
                <Body4 color={colors.lightGrey}>Stop</Body4>
                <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                  {isFutures && (
                    <UnitSelector
                      value={valueUnit}
                      onChange={setValueUnit}
                      disabled={!isEnabled}
                    />
                  )}
                  <NumericalInput
                    disabled={!isEnabled}
                    icon={dollarIcon}
                    width="30px"
                    maxHeight="23px"
                    fontSize={fontSizes.body4}
                    fontColor="white"
                    backgroundColor="grey"
                    controlledValue={stopPerBracket}
                    onChange={setStopPerBracket}
                  />
                </div>
              </div>
            </>
          )}

          <div className={style.labelAndControl}>
            <Body4 color={colors.lightGrey}>Percent</Body4>
            <NumericalInput
              disabled={!isEnabled}
              icon="%"
              width="30px"
              maxHeight="23px"
              fontSize={fontSizes.body4}
              fontColor="white"
              backgroundColor="grey"
              controlledValue={qtyBasisValuePerBracket}
              onChange={setQtyBasisValuePerBracket}
            />
          </div>
        </div>
      )}
    </div>
  );
});
